import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductGallery from "./components/ProductGallery";
import { useProduct } from "@/hooks/useProduct";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { galleryFor } from "@/lib/productImages";
import { enquireHref } from "@/lib/enquire";
import type { SparePart } from "@/mocks/products";

const CATEGORY_PATHS: Record<SparePart["category"], string> = {
  Engines: "/engines",
  Transmissions: "/transmissions",
  "Body Parts": "/body-parts",
};

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-neutral-50 px-4 py-3">
      <p className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-brand-dark">{value || "—"}</p>
    </div>
  );
}

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { product, loading, error } = useProduct(id);
  const { pricing, site } = useSiteSettings();
  const showPrices = pricing.show_prices;

  useEffect(() => {
    if (product) {
      document.title = `${product.name} | ${site.site_name}`;
    }
  }, [product, site.site_name]);

  return (
    <div className="min-h-screen bg-white font-body">
      <Navbar />
      <main className="w-full bg-neutral-50">
        {/* Breadcrumb band */}
        <section className="w-full bg-brand-dark py-8 md:py-10">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav className="flex flex-wrap items-center gap-2 text-xs text-white/60">
              <Link to="/" className="transition hover:text-brand-gold">
                {t("detail.home")}
              </Link>
              <span>/</span>
              {product && (
                <>
                  <Link
                    to={CATEGORY_PATHS[product.category]}
                    className="transition hover:text-brand-gold"
                  >
                    {product.category}
                  </Link>
                  <span>/</span>
                  <span className="text-brand-gold">{product.name}</span>
                </>
              )}
            </nav>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
          {loading ? (
            <div className="flex h-72 items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-brand-gold"></i>
            </div>
          ) : !product ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-neutral-300 bg-white py-20 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                <i className="ri-error-warning-line text-2xl"></i>
              </span>
              <p className="mt-4 text-sm text-neutral-600">
                {error || t("detail.notFound")}
              </p>
              <Link
                to="/"
                className="mt-4 rounded-md bg-brand-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
              >
                {t("detail.backHome")}
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
              {/* Gallery */}
              <ProductGallery
                images={galleryFor(product, 0)}
                alt={`${product.name} - used auto parts Australia`}
              />

              {/* Info */}
              <div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded-full bg-brand-gold/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-brand-dark">
                    {product.category}
                  </span>
                  <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                    {product.condition}
                  </span>
                  <span className="rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-900">
                    {product.stock}
                  </span>
                </div>

                <h1 className="mt-4 font-heading text-2xl md:text-3xl font-semibold uppercase text-brand-dark">
                  {product.name}
                </h1>

                {showPrices && (
                  <p className="mt-3 font-heading text-3xl font-semibold text-brand-dark">
                    {product.price}
                  </p>
                )}

                <p className="mt-4 text-sm leading-relaxed text-neutral-600">
                  {product.description}
                </p>

                {/* Specs */}
                <h4 className="mt-8 text-xs font-semibold uppercase tracking-widest text-brand-dark">
                  {t("detail.specifications")}
                </h4>
                <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
                  <Spec label={t("detail.make")} value={product.make} />
                  <Spec label={t("detail.model")} value={product.model} />
                  <Spec label={t("detail.year")} value={product.yearRange} />
                  <Spec label={t("detail.fitment")} value={product.fitment} />
                  <Spec label={t("detail.condition")} value={product.condition} />
                  <Spec label={t("detail.warranty")} value={product.warranty} />
                </div>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
                  <a
                    href={enquireHref(product, showPrices)}
                    className="flex flex-1 items-center justify-center gap-2 rounded-md bg-brand-gold px-6 py-3 text-sm font-semibold uppercase tracking-wide text-brand-dark transition hover:bg-brand-dark hover:text-white whitespace-nowrap"
                  >
                    <i className="ri-message-3-line"></i>
                    {t("grid.enquire")}
                  </a>
                  <Link
                    to={CATEGORY_PATHS[product.category]}
                    className="flex items-center justify-center gap-2 rounded-md border border-neutral-300 px-6 py-3 text-sm font-medium text-neutral-600 transition hover:bg-neutral-100 whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    {t("detail.backToList")}
                  </Link>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-neutral-100 pt-5 text-xs text-neutral-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-truck-line text-brand-gold"></i>
                    {t("grid.shipping")}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-shield-check-line text-brand-gold"></i>
                    {product.warranty}
                  </span>
                </div>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}